/*
 *
 * Copyright 2019-2022, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */
import React from 'react';
import {Card, Elevation} from "@blueprintjs/core";
import {WrcServer} from "./WrcServer.js";
import {IconNames} from "@blueprintjs/icons";
import {Intent} from "@blueprintjs/core/lib/esm/index";
import {WrcDataTable} from "./WrcDataTables.js";
import {Button, Popover, PopoverInteractionKind, Position} from "@blueprintjs/core";
import './WrcStatusDashboard.css';

export class WrcStatusDashboard extends React.Component {
    constructor(props) {
        super(props);
        
        this.state = {
            is_loaded: false,
            dashboard_data: [],
            dashboard_fields: []
        };
    }

    componentDidMount() {
        let forms = this.props.db_schema.filter((e) => e.form_id !== undefined).map(function(e) {
            return e.form_id;
        });
        WrcServer.post('get-form-meta-data',
                        {form_ids: forms, table_schema: this.props.table_schema},
                        (content) => this.getFormData(content)
        );
    }

     // for when the record ID changes
    componentDidUpdate(prevProps, prevState){
        if (prevProps.record_id !== this.props.record_id) {
            WrcServer.post('get-dashboard-data',
                {table_schema: this.props.table_schema, db_schema: this.props.db_schema, table_name: this.props.table_name,
                record_id: this.props.record_id},
                (content) => this.getData(content)
            );
        }
    };

    getData(content) {
        this.setState({
            dashboard_data: Object.values(content),
            is_loaded: true
        });
    }

    getFormData(form_data) {
        let fields = [];
        let multi_tab = [];
        let form = null;
        for (const [k, v] of Object.entries(form_data)) { // eslint-disable-line
            if (!('props' in v)) {
                // no form fields in here. move on
                continue;
            }
            form = JSON.parse(v['props'])
            if ('tabs' in form) { 
                multi_tab = []
                for (let i=0;i<form.tabs.length;i++) {
                    multi_tab.push(form.tabs[i].elements);
                }
                multi_tab = [].concat(...multi_tab);
                fields[form.table_name] = multi_tab;
            }
            if ('elements' in form) {
                fields[form.table_name] = form.elements;
            }
        }
        this.setState({
            dashboard_fields: fields,
        });
        if (this.props.record_id !== null && this.props.record_id !== undefined) {
            WrcServer.post('get-dashboard-data',
                            {table_schema: this.props.table_schema, db_schema: this.props.db_schema, table_name: this.props.table_name,
                            record_id: this.props.record_id},
                            (content) => this.getData(content)
            );
        } else {
            this.setState({
                is_loaded: true
            });
        }
    }

    checkComplete(dta, tbl, fields) {
        let field_check = {};
        let field_labels = {};
        let show = null;
        let disabled = null;
        let field = null;
        let ignore_fields = {}
        for (let i=0; i<this.props.db_schema.length; i++) {
            ignore_fields[this.props.db_schema[i].name] = this.props.db_schema[i].ignore_fields !== undefined ? this.props.db_schema[i].ignore_fields : [];
        }
        for (let i=0; i<fields.length; i++) {
            // some fields use table_name instead of column name (multi checkbox)
            field = fields[i].column_name !== undefined ? fields[i].column_name : fields[i].table_name !== undefined ? fields[i].table_name : null;
            // neither of our field identifiers are present. probably an instructions field or something that doesn't contain data
            if (field === null) {
                continue;
            }
            if (ignore_fields[tbl].includes(field)) {
                continue;
            }
            // if this field has a _not_reported checkbox and it is checked, the it's good
            if ('include_not_reported' in fields[i] &&
                `${field}_not_reported` in dta &&
                dta[`${field}_not_reported`] === 't') {
                continue;
            }
            field_labels[field] = fields[i].label;
            if ('show_if' in fields[i] || 'disable_if' in fields[i]) {
                show = true;
                if ('show_if' in fields[i]) {
                    for (let ii=0; ii<fields[i].show_if.length; ii++) {
                        // wild card check means any value passes
                        if (fields[i].show_if[ii].values.includes('*') && dta[fields[i].show_if[ii].field_name] !== null) {
                            break;
                        }
                        // some field in the check is missing so it's an invalid field
                        if (!fields[i].show_if[ii].values.includes(dta[fields[i].show_if[ii].field_name]) || dta[fields[i].show_if[ii].field_name] === null) {
                            show = false;
                            break;
                        }
                    }
                }
                disabled = false
                if ('disable_if' in fields[i]) {
                    for (let ii=0; ii<fields[i].disable_if.length; ii++) {
                        // wild card check means any value passes
                        if (fields[i].disable_if[ii].values.includes('*') && dta[fields[i].disable_if[ii].field_name] !== null) {
                            disabled = true;
                            break;
                        }
                        // some field in the check is missing so it's an invalid field
                        if (fields[i].disable_if[ii].values.includes(dta[fields[i].disable_if[ii].field_name])) {
                            disabled = true;
                            break;
                        }
                    }
                }
                if (show && !disabled) {
                    field_check[field] = {'check': false};
                    if (dta[field] !== null && dta[field] !== undefined) {
                        field_check[field].check = true;
                    }
                }
            }
            else {
                field_check[field] = {'check': false};
                if (dta[field] !== null && dta[field] !== undefined) {
                    field_check[field].check = true;
                }
            }
        }
        let missing_fields = Object.keys(field_check).filter((e) => field_check[e].check === false && field_labels[e] !== undefined).map(function(key, val) {
            return field_labels[key];
        });
        let check = 'complete';
        for (const [k, v] of Object.entries(field_check)) { // eslint-disable-line
            if (v.check === false) {
                check = 'partial';
                break;
            }
        }
        return {'check': check, 'missing_fields': missing_fields};
    }

    render() {
        let ld_full_class = 'dashboard-v';
        let ld_list_class = 'dashboard-LD-v';

        if (this.state.is_loaded && this.state.dashboard_data && this.props.record_id !== null && this.props.record_id !== undefined &&
            this.state.dashboard_fields !== undefined) {
            let ld_card_alignment= 'left';
            ld_full_class += " ";
            ld_full_class += ld_card_alignment;
            let tables = this.props.db_schema.filter((tbl) => !('field' in tbl)).map(function(tbl) {
                return tbl.name;
            });
            let tbl_headers = this.props.db_schema.filter((tbl) => !('field' in tbl)).map(function(tbl) {
                return (<th>{tbl.label}</th>);
            });
            let pop_content = null;
            let missing_fields = null;
            let check_multi_row_table = false;
            let multi_row_table = [];
            let multi_missing_fields = [];
            let data_check = {};
            let wrc_record_not_complete = 0;
            let rows = this.state.dashboard_data.map(function(data, id) {
                let cells = tables.map(function(table, table_index) {
                    if (table in data) {
                        if (data[table].length > 1) {
                            multi_row_table = [];
                            missing_fields = {};
                            wrc_record_not_complete = 0;
                            for (let i=0; i<data[table].length; i++) {
                                data_check = this.checkComplete(data[table][i], table, this.state.dashboard_fields[table]);
                                multi_row_table.push(data_check.check);
                                missing_fields[i+1] = data_check.missing_fields;
                                if ('wrc_record_complete' in data[table][i] && data[table][i]['wrc_record_complete'] !== 't') {
                                    wrc_record_not_complete++;
                                }
                            }
                            check_multi_row_table = multi_row_table.every((val, i, arr) => val === 'complete');
                            if (check_multi_row_table) {
                                if (!wrc_record_not_complete) {
                                    return (<td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                    <Button icon={IconNames.CONFIRM}
                                            intent={Intent.SUCCESS}
                                            minimal={true}
                                            active={true}
                                            />
                                </td>);
                                } else {
                                    let plural, question;
                                    if (wrc_record_not_complete == 1) {
                                        plural = '';
                                        question = 'Is it?';
                                    } else {
                                        plural = 's';
                                        question = 'Are they?';
                                    }
                                    pop_content = (
                                        <div>
                                            <h3 className="dashboard-header center">Record{plural} Complete?</h3>
                                            <ul className="dashboard-list">
                                                <li>No required fields missing, but {wrc_record_not_complete} record{plural} not marked as complete. {question}</li>
                                            </ul>
                                        </div>
                                    );
                                    return (<td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                        <Popover content={pop_content}
                                                interactionKind={PopoverInteractionKind.CLICK}
                                                position={Position.RIGHT}
                                                fill={false}
                                        >
                                            <Button icon={IconNames.HELP}
                                                    intent={Intent.PRIMARY}
                                                    minimal={true}
                                                    active={true}
                                                    />
                                        </Popover>
                                    </td>);
                                }
                                return (<td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                    <Button icon={IconNames.CONFIRM}
                                            intent={Intent.SUCCESS}
                                            minimal={true}
                                            active={true}
                                            />
                                </td>);
                            }
                            else {
                                missing_fields = Object.keys(missing_fields).filter((id) => missing_fields[id].length > 0).map(function(id) {
                                    multi_missing_fields = missing_fields[id].map(function(e) {
                                        return (<li>{e}</li>);
                                    })
                                    return (<ul className="dashboard-list">
                                                <h4 className="dashboard-header center">ID# {id}</h4>
                                                {multi_missing_fields}
                                            </ul>
                                            );
                                });

                                pop_content = (
                                    <div>
                                            <h3 className="dashboard-header center">Missing Fields</h3>
                                            {missing_fields}
                                    </div>
                                );
                                let button_icon, button_intent;
                                if (data[table].length == missing_fields.length) {
                                    // if ALL records are missing some fields
                                    button_icon = IconNames.DELETE;
                                    button_intent = Intent.DANGER;
                                } else {
                                    // if only SOME but not all records are missing some fields
                                    button_icon = IconNames.DISABLE;
                                    button_intent = Intent.WARNING;
                                }
                                return (<td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                    <Popover content={pop_content}
                                            interactionKind={PopoverInteractionKind.CLICK}
                                            position={Position.RIGHT}
                                            fill={false}
                                    >
                                    <Button icon={button_icon}
                                            intent={button_intent}
                                            minimal={true}
                                            active={true}
                                            />
                                    </Popover>
                                </td>);
                            }
                        }
                        else {
                            data_check = this.checkComplete(data[table][0], table, this.state.dashboard_fields[table]);
                            if (data_check.check === 'complete') {
                                if ('wrc_record_complete' in data[table][0] && data[table][0]['wrc_record_complete'] !== 't') {
                                    pop_content = (
                                        <div>
                                            <h3 className="dashboard-header center">Record Complete?</h3>
                                            <ul className="dashboard-list">
                                                <li>No required fields missing, but record not marked as complete. Is it?</li>
                                            </ul>
                                        </div>
                                    );
                                    return (<td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                        <Popover content={pop_content}
                                                interactionKind={PopoverInteractionKind.CLICK}
                                                position={Position.RIGHT}
                                                fill={false}
                                        >
                                            <Button icon={IconNames.HELP}
                                                    intent={Intent.PRIMARY}
                                                    minimal={true}
                                                    active={true}
                                                    />
                                        </Popover>
                                    </td>);
                                } else {
                                    return (<td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                        <Button icon={IconNames.CONFIRM}
                                                intent={Intent.SUCCESS}
                                                minimal={true}
                                                active={true}
                                                />
                                    </td>);
                                }
                            }
                            else if (data_check.check === 'partial') {
                                missing_fields = data_check.missing_fields.map(function(e) {
                                    return (<li>{e}</li>);
                                });
                                pop_content = (
                                    <div>
                                        <h3 className="dashboard-header center">Missing Fields</h3>
                                        <ul className="dashboard-list">
                                        {missing_fields}
                                        </ul>
                                    </div>
                                );
                                return (<td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                    <Popover content={pop_content}
                                            interactionKind={PopoverInteractionKind.CLICK}
                                            position={Position.RIGHT}
                                            fill={false}
                                    >
                                        <Button icon={IconNames.DELETE}
                                                intent={Intent.DANGER}
                                                minimal={true}
                                                active={true}
                                                />
                                    </Popover>
                                </td>);
                            }

                        }
                    }
                    return (<td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                        <Button icon={IconNames.CIRCLE}
                                intent={Intent.PRIMARY}
                                minimal={true}
                                active={true}
                                />
                    </td>);
                }.bind(this));
                return (
                    <tr>
                        <td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                            {this.props.record_id}
                        </td>
                        {cells}
                    </tr>
                );
            }.bind(this));
            let hidden_columns = [];
            let options = {};
            let scrollX = true;
            /* if one row or less, then hide Patient ID column */
            if (rows.length <= 1) {
                hidden_columns = [0];
                options = {
                    "info": false,
                    "ordering": false
                };
                scrollX = false;
            }
            let table = (
                <WrcDataTable
                    index={this.props.component_index}
                    entity={this.props.entity}
                    page_length={5}
                    show_search={false}
                    noWrapHeader={false}
                    scrollX={scrollX}
                    disableScrollY={true}
                    disableToolbar={true}
                    options={options}
                    hidden_columns={hidden_columns}
                >
                    <thead>
                        <tr>
                            <th>Patient ID</th>
                            {tbl_headers}
                        </tr>
                    </thead>
                    <tbody>
                        {rows}
                    </tbody>
                </WrcDataTable>
            );
            return (
                <div className={ld_full_class}>
                    <div className={ld_list_class}>
                        <Card interactive={false} elevation={Elevation.TWO} style={{minHeight:'430'}}>
                            {table}
                        </Card>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={ld_full_class}>
                    <div className={ld_list_class}>
                        <Card style={{height:'430'}}><h3>Loading...</h3></Card>
                    </div>
                </div>
            );
        }
    }
}

export default WrcStatusDashboard;
